// src/components/MemoraGuard.js
// Protege as rotas do Memor.IA: só renderiza a página se o usuário logado
// tiver memora_ia = 'sim' na auth_user. Caso contrário, mostra aviso de sem acesso.
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import MemoraAnimacao from './MemoraAnimacao';

// Mascote animado (Lottie) fixo no canto, presente em todas as páginas do Memor.IA.
// A animação é servida pelo próprio front (public/memora_mascote.json) — o iframe do
// lottie.host sumia quando o site externo era bloqueado (rede/extensão) ou ficava fora do ar.
const MascoteMemoraIA = () => (
  <div
    aria-hidden="true"
    style={{
      position: 'fixed',
      right: 18,
      bottom: 18,
      width: 85,
      height: 85,
      pointerEvents: 'none',
      zIndex: 60,
    }}
  >
    <MemoraAnimacao tamanho={85} />
  </div>
);

const MemoraGuard = ({ children, adminOnly = false, grupos = null, semMascote = false }) => {
  // null = verificando | 'ok' | 'sem_memora' | 'sem_adm'
  const [estado, setEstado] = useState(null);
  const navigate = useNavigate();

  // grupos permitidos: prop `grupos` (ex.: ['adm','comercial']) ou atalho adminOnly
  const gruposPermitidos = grupos || (adminOnly ? ['adm'] : null);

  useEffect(() => {
    const token = localStorage.getItem('access');
    if (!token) {
      setEstado('sem_memora');
      return;
    }
    axios
      .get(`${process.env.REACT_APP_API_BASE}/api/verificar-grupo/`, {
        headers: { Authorization: `Bearer ${token}` },
      })
      .then((res) => {
        const temMemora = (res.data.memora_ia || '').toLowerCase().trim() === 'sim';
        const grupoUser = (res.data.grupo || '').toLowerCase().trim();
        if (!temMemora) setEstado('sem_memora');
        else if (gruposPermitidos && !gruposPermitidos.includes(grupoUser)) setEstado('sem_adm');
        else setEstado('ok');
      })
      .catch(() => setEstado('sem_memora'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminOnly, grupos]);

  if (estado === null) {
    return (
      <div className="container-fluid mt-5 text-center">
        <div className="spinner-border text-primary" style={{ width: '3rem', height: '3rem' }}></div>
        <p className="mt-3 text-muted">Verificando permissão...</p>
      </div>
    );
  }

  if (estado !== 'ok') {
    const restritoAdm = estado === 'sem_adm';
    return (
      <div className="das-page">
        <div className="container-fluid mt-5">
          <div className="row justify-content-center">
            <div className="col-md-6 col-lg-5">
              <div className="card border-0 shadow-sm text-center">
                <div className="card-body py-5 px-4">
                  <div
                    className="mx-auto mb-4 d-flex align-items-center justify-content-center"
                    style={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      background: 'rgba(214, 16, 181, 0.1)',
                    }}
                  >
                    <i className="fa-solid fa-lock fa-2x" style={{ color: '#d610b5' }}></i>
                  </div>
                  <h4 className="fw-bold mb-2">
                    {restritoAdm ? 'Área restrita a administradores' : 'Acesso não liberado'}
                  </h4>
                  {restritoAdm ? (
                    <p className="text-muted mb-4">
                      Esta área do <strong>Memor.IA</strong> é exclusiva para perfis
                      autorizados{gruposPermitidos ? ` (${gruposPermitidos.join(', ')})` : ''}.
                      Se você precisa deste acesso, fale com o administrador do sistema.
                    </p>
                  ) : (
                    <>
                      <p className="text-muted mb-1">
                        Você ainda não tem permissão para acessar o <strong>Memor.IA</strong>.
                      </p>
                      <p className="text-muted mb-4">
                        A plataforma está em fase de implantação. Se você deveria fazer parte
                        do piloto, fale com o administrador do sistema.
                      </p>
                    </>
                  )}
                  <button
                    className="btn"
                    onClick={() => navigate('/Contabilidade')}
                    style={{ backgroundColor: '#00205b', borderColor: '#00205b', color: '#fff' }}
                  >
                    <i className="fas fa-arrow-left me-2"></i>Voltar ao início
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      {!semMascote && <MascoteMemoraIA />}
      {children}
    </>
  );
};

export default MemoraGuard;
